/**
 * LYMI wishlist: saved products kept in this browser only (localStorage). No app, server or customer account; the
 * list is not shared with other browsers or devices and is lost if the browser's site data is cleared.
 *
 * What is stored: for each saved product, its id and handle (and when it was saved). Never prices, titles or stock;
 * pages always show the store's current data. Products are saved, not individual sizes.
 *
 * - `wishlistStore` reads and writes the list. Every write is read back to confirm it took; a failed write (storage
 *   blocked, full, or switched off) is reported as a failure, never as a save.
 * - Changes are announced to the rest of the page (`lymi:wishlist-change` on document) and to other tabs (the
 *   browser's `storage` event), so every heart for a product shows the same state.
 * - `<wishlist-button>` is the heart on a product card. It stays hidden until storage is confirmed to work in this
 *   browser, so no heart ever pretends to save.
 * - Nothing here removes a saved product by itself.
 */

export const STORAGE_KEY = 'lymi:wishlist:v1';
export const MAX_ITEMS = 100;
export const CHANGE_EVENT = 'lymi:wishlist-change';

const ID_PATTERN = /^\d{1,20}$/;
const HANDLE_PATTERN = /^[^\s/?#\\]{1,255}$/;

/**
 * Turns whatever is stored into a clean list. Unreadable or unexpected data gives an empty list rather than an error.
 * @param {string | null} raw
 * @returns {Array<{ id: string, handle: string, addedAt: number }>}
 */
export function parseStore(raw) {
  if (!raw) return [];
  let data;
  try {
    data = JSON.parse(raw);
  } catch {
    return [];
  }
  const items = Array.isArray(data?.items) ? data.items : [];
  /** @type {Array<{ id: string, handle: string, addedAt: number }>} */
  const clean = [];
  const seen = new Set();
  for (const item of items) {
    const id = String(item?.id ?? '');
    const handle = String(item?.handle ?? '');
    if (!ID_PATTERN.test(id) || !HANDLE_PATTERN.test(handle) || seen.has(id)) continue;
    seen.add(id);
    clean.push({ id, handle, addedAt: Number.isFinite(item?.addedAt) ? item.addedAt : 0 });
    if (clean.length >= MAX_ITEMS) break;
  }
  return clean;
}

/**
 * @param {Array<{ id: string, handle: string, addedAt: number }>} items
 * @returns {string}
 */
export function serializeStore(items) {
  return JSON.stringify({ version: 1, items });
}

/**
 * Adds a product (newest first). Returns the same list when it is already saved, or `null` when the list is full.
 * @param {Array<{ id: string, handle: string, addedAt: number }>} items
 * @param {{ id: string, handle: string }} product
 * @param {number} [now]
 */
export function addItem(items, product, now = Date.now()) {
  if (items.some((item) => item.id === product.id)) return items;
  if (items.length >= MAX_ITEMS) return null;
  return [{ id: product.id, handle: product.handle, addedAt: now }, ...items];
}

/**
 * @param {Array<{ id: string, handle: string, addedAt: number }>} items
 * @param {string} id
 */
export function removeItem(items, id) {
  return items.filter((item) => item.id !== id);
}

/**
 * @param {{ id: string, handle: string }} product
 */
export function isValidProduct(product) {
  return ID_PATTERN.test(String(product?.id ?? '')) && HANDLE_PATTERN.test(String(product?.handle ?? ''));
}

/** @type {boolean | null} */
let storageWorks = null;

/**
 * Whether this browser actually lets the page keep data. Tested once by writing and reading a probe value; some
 * private windows allow it and some do not, so nothing is assumed.
 */
function storageAvailable() {
  if (storageWorks !== null) return storageWorks;
  try {
    const probe = `${STORAGE_KEY}:probe`;
    window.localStorage.setItem(probe, '1');
    storageWorks = window.localStorage.getItem(probe) === '1';
    window.localStorage.removeItem(probe);
  } catch {
    storageWorks = false;
  }
  return storageWorks;
}

function readItems() {
  try {
    return parseStore(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return [];
  }
}

/**
 * @param {Array<{ id: string, handle: string, addedAt: number }>} items
 * @param {string} id
 * @param {boolean} expectSaved
 * @returns {boolean} whether the stored list now matches what was asked for
 */
function writeItems(items, id, expectSaved) {
  try {
    window.localStorage.setItem(STORAGE_KEY, serializeStore(items));
    const check = readItems().some((item) => item.id === id);
    return check === expectSaved;
  } catch {
    return false;
  }
}

/** @param {string} id @param {boolean} saved */
function announceChange(id, saved) {
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { id, saved } }));
}

export const wishlistStore = {
  available: storageAvailable,
  items: readItems,
  /** @param {string} id */
  has(id) {
    return readItems().some((item) => item.id === id);
  },
  /**
   * @param {{ id: string, handle: string }} product
   * @returns {'saved' | 'full' | 'failed'}
   */
  add(product) {
    if (!storageAvailable() || !isValidProduct(product)) return 'failed';
    const next = addItem(readItems(), product);
    if (next === null) return 'full';
    if (!writeItems(next, product.id, true)) return 'failed';
    announceChange(product.id, true);
    return 'saved';
  },
  /**
   * @param {string} id
   * @returns {'removed' | 'failed'}
   */
  remove(id) {
    if (!storageAvailable()) return 'failed';
    if (!writeItems(removeItem(readItems(), id), id, false)) return 'failed';
    announceChange(id, false);
    return 'removed';
  },
};

// Another tab changed the list: tell this page.
window.addEventListener('storage', (event) => {
  if (event.key === STORAGE_KEY || event.key === null) {
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { id: null } }));
  }
});

/**
 * Tells the page's visible notice (assets/lymi-notice.js) the result of a shopper's own wishlist action. Sent only
 * from clicks, never when saved state loads or another tab changes the list.
 * @param {'saved' | 'removed' | 'full' | 'failed'} result
 * @param {Element | null} source - The control that was used, so the notice can stay clear of it.
 */
export function reportResult(result, source) {
  document.dispatchEvent(new CustomEvent('lymi:wishlist-result', { detail: { result, source } }));
}

/**
 * One polite live region for wishlist messages, shared by every heart and the wishlist page.
 * @param {string | undefined} message
 */
export function announce(message) {
  if (!message) return;
  let region = document.getElementById('LymiWishlistStatus');
  if (!region) {
    region = document.createElement('div');
    region.id = 'LymiWishlistStatus';
    region.className = 'visually-hidden';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    document.body.append(region);
  }
  // Clear first so the same message is announced again on a repeat action.
  region.textContent = '';
  window.setTimeout(() => {
    if (region) region.textContent = message;
  }, 50);
}

/**
 * The heart. Markup: <wishlist-button data-product-id data-product-handle data-message-saved data-message-removed
 * data-message-failed data-message-full hidden><button type="button" aria-pressed="false" aria-label="…">…</button>.
 * Card refreshes (filtering, sorting, pagination, option changes) may replace or patch the markup; the heart re-reads
 * the saved state whenever it is connected or its product, pressed state or visibility is changed from outside.
 */
class WishlistButton extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  button = null;
  /** @type {MutationObserver | null} */
  observer = null;

  connectedCallback() {
    this.button = this.querySelector('button');
    if (!this.button || !wishlistStore.available()) {
      this.hidden = true;
      return;
    }
    this.hidden = false;
    this.button.addEventListener('click', this.#handleClick);
    document.addEventListener(CHANGE_EVENT, this.#sync);
    this.observer = new MutationObserver(this.#sync);
    // An in-place refresh (morph) copies the server markup's `hidden` back; storage was confirmed, so show it again.
    this.observer.observe(this, { attributes: true, attributeFilter: ['data-product-id', 'hidden'] });
    this.observer.observe(this.button, { attributes: true, attributeFilter: ['aria-pressed'] });
    this.#sync();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#handleClick);
    document.removeEventListener(CHANGE_EVENT, this.#sync);
    this.observer?.disconnect();
    this.observer = null;
  }

  get product() {
    return { id: this.dataset.productId || '', handle: this.dataset.productHandle || '' };
  }

  #sync = () => {
    if (!this.button) return;
    if (this.hidden) this.hidden = false;
    const saved = wishlistStore.has(this.product.id) ? 'true' : 'false';
    // Only write when different, so the observer above does not loop.
    if (this.button.getAttribute('aria-pressed') !== saved) this.button.setAttribute('aria-pressed', saved);
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    // The heart is its own action: never the card's link, image or purchase controls.
    event.preventDefault();
    event.stopPropagation();

    const saved = this.button?.getAttribute('aria-pressed') === 'true';
    if (saved) {
      const result = wishlistStore.remove(this.product.id);
      announce(result === 'removed' ? this.dataset.messageRemoved : this.dataset.messageFailed);
      reportResult(result, this.button);
    } else {
      const result = wishlistStore.add(this.product);
      if (result === 'saved') announce(this.dataset.messageSaved);
      else if (result === 'full') announce(this.dataset.messageFull);
      else announce(this.dataset.messageFailed);
      reportResult(result, this.button);
    }
    this.#sync();
  };
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}

/**
 * Builds the wishlist link's accessible label from its templates (no visible count is shown).
 * @param {number} count
 * @param {{ zero?: string, one?: string, other?: string }} labels - "[count]" is replaced by the number
 */
export function countLabel(count, labels) {
  const template = count === 0 ? labels.zero : count === 1 ? labels.one : labels.other;
  return (template || '').replace('[count]', String(count));
}

/**
 * The visible count on the wishlist badge: nothing at 0, the exact number from 1 to 10, then "10+". Only the badge is
 * capped; the list itself keeps its own limit (MAX_ITEMS) and the accessible name always has the exact number.
 * @param {number} count
 * @returns {string}
 */
export function badgeText(count) {
  if (!Number.isFinite(count) || count <= 0) return '';
  return count > 10 ? '10+' : String(Math.floor(count));
}

/**
 * The header heart and the menu-drawer link (`[data-wishlist-trigger]`) carry the saved count in their accessible
 * name (the link's aria-label, or a hidden `[data-wishlist-count-text]`) and on their count badge
 * (`[data-wishlist-bubble]`, aria-hidden). Left as plain "Wishlist" with no badge when this browser cannot keep a list.
 * Runs on load (so earlier saves count at once), on every change in this page and, through the storage event, in
 * other tabs.
 */
function updateTriggers() {
  if (!wishlistStore.available()) return;
  const count = wishlistStore.items().length;
  for (const trigger of document.querySelectorAll('[data-wishlist-trigger]')) {
    if (!(trigger instanceof HTMLElement)) continue;
    const label = countLabel(count, {
      zero: trigger.dataset.wishlistLabelZero,
      one: trigger.dataset.wishlistLabelOne,
      other: trigger.dataset.wishlistLabelOther,
    });
    if (!label) continue;
    const hiddenText = trigger.querySelector('[data-wishlist-count-text]');
    if (hiddenText) hiddenText.textContent = label;
    else trigger.setAttribute('aria-label', label);

    const bubble = trigger.querySelector('[data-wishlist-bubble]');
    const bubbleCount = bubble?.querySelector('[data-wishlist-bubble-count]');
    if (bubble instanceof HTMLElement && bubbleCount) {
      const text = badgeText(count);
      bubbleCount.textContent = text;
      bubble.hidden = text === '';
    }
  }
}

document.addEventListener(CHANGE_EVENT, updateTriggers);
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', updateTriggers);
else updateTriggers();

// The count is read again whenever it could have gone stale without a change event in this page: a menu (or any
// other <details>) holding a wishlist link opens ("toggle" does not bubble, so it is caught on the way down); the
// page comes back from the browser's back/forward cache, where it kept the count it had when it was left; or the
// tab becomes visible again.
document.addEventListener(
  'toggle',
  (event) => {
    const details = event.target;
    if (details instanceof HTMLDetailsElement && details.open && details.querySelector('[data-wishlist-trigger]')) {
      updateTriggers();
    }
  },
  true
);
window.addEventListener('pageshow', (event) => {
  if (event.persisted) updateTriggers();
});
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') updateTriggers();
});
